/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { useAuthHttp } from '@hooks';
import { FLOX_LOGO_BLUE_SRC, RESET_PASSWORD_URL } from '@constants';
import {
  getPasswordStrength,
  PasswordStrengthBar,
} from '@components/PasswordStrengthBar';
import {
  ExclamationTriangleIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../tailwindComponents/Button';

interface ResetPasswordValues {
  password: string;
  hasAgreedToTandC: boolean;
}

const getValidationSchema = (requireTandC: boolean) =>
  Yup.object({
    password: Yup.string()
      .min(8, 'Password must have at least 8 characters.')
      .required('A new password is required.')
      .test({
        name: 'password-strength-test',
        test: (value, ctx) => {
          if (!(getPasswordStrength(value as string) > 1)) {
            return ctx.createError({
              message: 'Password is not strong enough.',
            });
          }
          return true;
        },
      }),
    hasAgreedToTandC: requireTandC
      ? Yup.boolean().oneOf(
          [true],
          'You must agree to the Terms and Conditions.'
        )
      : Yup.boolean(),
  });

export function ResetPassword() {
  const authHttp = useAuthHttp();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');

  useDocumentTitle('FLOX - Reset Password');

  const hasAgreedToTandC = searchParams.get('hastandc') === 'true';

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ResetPasswordValues>({
    resolver: yupResolver(getValidationSchema(!hasAgreedToTandC)),
    mode: 'onChange',
    defaultValues: {
      password: '',
      hasAgreedToTandC,
    },
  });

  const passwordValue = watch('password');

  const onSubmit = async (values: ResetPasswordValues): Promise<void> => {
    setResetPasswordError('');

    await authHttp
      .post(RESET_PASSWORD_URL, {
        token: searchParams.get('token'),
        password: values.password,
        hasAgreedToTandC: values.hasAgreedToTandC,
      })
      .then(() => {
        reset();
        setResetPasswordSuccess(true);
      })
      .catch(() => {
        reset();
        setResetPasswordError('Something went wrong. Please try again.');
      });
  };

  return (
    <main className='flex min-h-screen w-full'>
      {/* Image Panel */}
      <section
        className='hidden md:block md:w-1/2 min-h-screen bg-cover bg-no-repeat bg-center transition-all duration-300'
        style={{ backgroundImage: 'url(/images/bg-login.png)' }}
      />

      {/* Form Panel */}
      <section className='flex-1 flex items-center justify-center bg-white px-4 py-12'>
        <div className='w-full max-w-md'>
          {/* Logo */}
          <div className='flex justify-center mb-8'>
            <img
              src={FLOX_LOGO_BLUE_SRC}
              alt='Flox logo'
              className='h-14 w-auto'
            />
          </div>

          {/* Title */}
          <div className='text-center mb-8'>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>
              {resetPasswordSuccess ? 'Password Reset!' : 'Reset your password'}
            </h1>
            <p className='text-gray-500'>
              {resetPasswordSuccess
                ? 'Your password has been successfully updated'
                : 'Enter a new password for your account'}
            </p>
          </div>

          {/* Card */}
          <div className='bg-white rounded-2xl shadow-lg border border-gray-100 p-8'>
            {resetPasswordSuccess ? (
              /* Success State */
              <div className='text-center'>
                <div className='flex justify-center mb-4'>
                  <div className='rounded-full bg-green-100 p-3'>
                    <CheckCircleIcon className='h-8 w-8 text-green-600' />
                  </div>
                </div>
                <p className='text-gray-600 mb-6'>
                  Your password was successfully reset. You can now log in with
                  your new password.
                </p>
                <Button
                  type='button'
                  intent='primary'
                  size='lg'
                  className='w-full'
                  label='Go to Login'
                  onClick={() => navigate('/login')}
                />
              </div>
            ) : (
              /* Form State */
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* Password Field */}
                <div className='mb-4'>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label
                    htmlFor='password'
                    className='block text-sm font-medium text-gray-700 mb-2'
                  >
                    New Password
                  </label>
                  <Controller
                    name='password'
                    control={control}
                    render={({ field }) => (
                      <div className='relative'>
                        <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                          <LockClosedIcon className='h-5 w-5 text-gray-400' />
                        </div>
                        <input
                          {...field}
                          type='password'
                          id='password'
                          placeholder='Enter a new password...'
                          autoComplete='new-password'
                          disabled={isSubmitting}
                          className={`block w-full rounded-lg border py-3 pl-10 pr-3 text-sm transition-colors ${
                            errors.password
                              ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                              : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white'
                          } focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                          onFocus={() => setPasswordFocus(true)}
                          onBlur={() => setPasswordFocus(false)}
                          onChange={(e) => {
                            field.onChange(e);
                            setPasswordStrength(
                              getPasswordStrength(e.target.value)
                            );
                          }}
                        />
                      </div>
                    )}
                  />
                  <p className='mt-1.5 text-xs text-gray-500'>
                    8 characters minimum, including a number.
                  </p>
                  {passwordFocus && passwordValue && (
                    <div className='mt-2'>
                      <PasswordStrengthBar score={passwordStrength} />
                    </div>
                  )}
                  {errors.password && (
                    <p className='mt-1.5 text-sm text-red-600'>
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Terms and Conditions Checkbox */}
                {!hasAgreedToTandC && (
                  <div className='mb-6'>
                    <Controller
                      name='hasAgreedToTandC'
                      control={control}
                      render={({ field }) => (
                        <label className='flex items-start gap-3 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={field.value}
                            onChange={field.onChange}
                            disabled={isSubmitting}
                            className='mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 focus:ring-offset-0 disabled:cursor-not-allowed'
                          />
                          <span className='text-sm text-gray-600'>
                            I agree to the{' '}
                            <a
                              href='/terms'
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-blue-600 hover:underline'
                            >
                              Terms and Conditions
                            </a>
                          </span>
                        </label>
                      )}
                    />
                    {errors.hasAgreedToTandC && (
                      <p className='mt-1.5 text-sm text-red-600'>
                        {errors.hasAgreedToTandC.message}
                      </p>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type='submit'
                  intent='primary'
                  size='lg'
                  disabled={!isValid || isSubmitting}
                  className='w-full'
                  label={
                    isSubmitting ? 'Resetting password...' : 'Reset password'
                  }
                />

                {/* Error Message - shown below button for visibility */}
                {resetPasswordError && (
                  <div className='rounded-lg bg-yellow-50 p-4 mt-4'>
                    <div className='flex'>
                      <ExclamationTriangleIcon className='h-5 w-5 text-yellow-400 flex-shrink-0' />
                      <p className='ml-3 text-sm text-yellow-700'>
                        {resetPasswordError}
                      </p>
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Footer Links */}
          <div className='mt-8 text-center'>
            <button
              type='button'
              onClick={() => navigate('/login')}
              className='inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline'
            >
              <ArrowLeftIcon className='h-4 w-4' />
              Back to Login
            </button>

            <p className='text-sm text-gray-400 mt-4'>
              Need help? Contact{' '}
              <a
                href='mailto:support@flox.is'
                className='text-blue-600 hover:underline'
              >
                support@flox.is
              </a>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
